import { useContext, useState } from "react";
import { trpc } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import type { OverdueSale } from "./RecaudoResults";

type Props = {
  overdueSales: OverdueSale[];
  callConfigId: string;
  activeCount: number;
  maxConcurrent: number;
  onSuccess: () => void;
};

const RecaudoVoiceConfirmation = ({
  overdueSales,
  callConfigId,
  activeCount,
  maxConcurrent,
  onSuccess,
}: Props) => {
  const { merchantId } = useContext(MerchantContext);
  const [started, setStarted] = useState(false);

  const startVoiceBatchMutation = trpc.collections.startVoiceBatch.useMutation({
    onSuccess: (data) => {
      setStarted(true);
      if (data.failed > 0 && data.initiated === 0) {
        toast.error(`No se pudo iniciar ninguna llamada (${data.failed} error(s))`);
      } else if (data.failed > 0) {
        toast.warning(
          `Se iniciaron ${data.initiated} llamada(s). ${data.failed} fallaron.`
        );
      } else {
        toast.success(`Se iniciaron ${data.initiated} llamada(s)`);
      }
      onSuccess();
    },
    onError: (error) => {
      if (error.data?.code === "PRECONDITION_FAILED") {
        toast.error(
          error.message || "Las llamadas solo se pueden realizar en horario de atención."
        );
      } else if (error.data?.code === "TOO_MANY_REQUESTS") {
        toast.error("No hay líneas disponibles en este momento. Intenta de nuevo más tarde.");
      } else {
        toast.error(error.message ?? "Error al iniciar las llamadas");
      }
    },
  });

  const availableSlots = maxConcurrent - activeCount;
  const contactsToCall = overdueSales.slice(0, availableSlots);

  const handleStart = () => {
    const contacts = contactsToCall.map((v) => ({
      phone: v.buyerMobile ?? v.phone ?? "",
      name: v.buyerFullName,
      saleId: v.saleId,
    }));

    startVoiceBatchMutation.mutate({
      merchantId: merchantId || "",
      callConfigId,
      contacts,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Confirmar llamadas</p>
        <p className="mb-4 text-sm text-gray-600">
          Revisa la información antes de iniciar las llamadas de cobranza.
        </p>
      </div>

      {/* Summary */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-purple-600">{contactsToCall.length}</p>
              <p className="text-sm text-gray-600">Llamadas a iniciar</p>
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-purple-600">
                {availableSlots}/{maxConcurrent}
              </p>
              <p className="text-sm text-gray-600">Líneas disponibles</p>
            </div>
            {activeCount > 0 && (
              <div className="flex flex-col items-center gap-1">
                <p className="text-3xl font-bold text-orange-500">{activeCount}</p>
                <p className="text-sm text-gray-600">Llamadas activas</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contacts list */}
      {contactsToCall.length > 0 && (
        <div className="max-h-[250px] overflow-y-auto space-y-2">
          {contactsToCall.map((v) => (
            <div
              key={v.saleId}
              className="flex items-start gap-3 rounded-md border border-gray-200 px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{v.buyerFullName}</p>
                <p className="text-sm text-gray-600">{v.buyerMobile ?? v.phone}</p>
                <p className="text-xs text-gray-500">
                  {v.overdueInstallmentCount} cuota(s) en mora · {v.maxDaysOverdue} día(s)
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {overdueSales.length > contactsToCall.length && (
        <Alert status="warning">
          <p className="text-sm">
            Solo se pueden iniciar <strong>{contactsToCall.length}</strong> de{" "}
            <strong>{overdueSales.length}</strong> llamadas por las líneas disponibles. Los contactos
            restantes no serán contactados en este lote.
          </p>
        </Alert>
      )}

      <Alert status="warning">
        <p className="text-sm">
          Al hacer clic en "Iniciar llamadas", se realizarán{" "}
          <strong>{contactsToCall.length}</strong> llamadas automáticas. Esta acción no se puede
          deshacer.
        </p>
      </Alert>

      <div className="flex justify-end">
        <Button
          onClick={handleStart}
          disabled={
            contactsToCall.length === 0 ||
            startVoiceBatchMutation.isLoading ||
            started
          }
        >
          {startVoiceBatchMutation.isLoading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Iniciando...
            </span>
          ) : (
            "Iniciar llamadas"
          )}
        </Button>
      </div>
    </div>
  );
};

export default RecaudoVoiceConfirmation;
